import { useMemo } from 'react';

/**
 * usePermissions Hook
 *
 * Provides permission-checking functionality for React components
 * Matches Laravel's @can() directive functionality
 *
 * Usage:
 *   const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
 *
 *   {hasPermission('users.edit') && <button>Edit</button>}
 *   {hasPermission('users.delete') && <button>Delete</button>}
 */
export const usePermissions = () => {
  // Get user from localStorage (set during login)
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;

  // Get user permissions array from user object
  const userPermissions = useMemo(() => {
    if (!user || !user.permissions) {
      return [];
    }

    // If permissions is already an array of strings, return it
    if (Array.isArray(user.permissions)) {
      return user.permissions;
    }

    // If permissions is an array of objects with 'name' property, extract names
    if (Array.isArray(user.permissions) && user.permissions.length > 0 && user.permissions[0].name) {
      return user.permissions.map(p => p.name);
    }

    return [];
  }, [user]);

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission name (e.g., 'users.edit', 'users.delete')
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!userPermissions || userPermissions.length === 0) {
      return false;
    }

    return userPermissions.includes(permission);
  };

  /**
   * Check if user has ANY of the specified permissions
   * @param {string[]} permissions - Array of permission names
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions) => {
    if (!Array.isArray(permissions)) {
      return false;
    }

    return permissions.some(permission => hasPermission(permission));
  };

  /**
   * Check if user has ALL of the specified permissions
   * @param {string[]} permissions - Array of permission names
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions) => {
    if (!Array.isArray(permissions)) {
      return false;
    }

    return permissions.every(permission => hasPermission(permission));
  };

  /**
   * Check if user has a specific role
   * @param {string} roleName - Role name (e.g., 'ADMIN', 'GUARDIAN')
   * @returns {boolean}
   */
  const hasRole = (roleName) => {
    if (!user || !user.role || !user.role.name) {
      return false;
    }

    return user.role.name === roleName;
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    user,
    permissions: userPermissions,
  };
};

export default usePermissions;
